import React, {useCallback, useEffect} from 'react'
import {Dictionary} from 'lodash'
import {RouteComponentProps, Link, useLocation, navigate} from '@reach/router'
import {useMutation} from '@apollo/client'
import {Formik, FormikProps, FormikConfig, FormikHelpers} from 'formik'
import qs from 'query-string'
import {Page, Container, Loader} from '../layout'
import {Field} from '../form/fields'
import {LockFill, PersonLinesFill} from 'react-bootstrap-icons'
import {accountConfirmMutation, tokenCreateMutation} from '../../contexts/user-queries'
import {AccountConfirm} from './_gql-types/AccountConfirm'
import {TokenCreate} from './_gql-types/TokenCreate'
import {useAuth} from '../../contexts/UserContext'


interface FormValues {
  email: string
  password: string
}

interface FormErrors {
  email?: string
  password?: string
}

interface QueryParams {
  email: string
  token: string
}

const reqMsg = 'Þennan reit þarf að fylla út'

// eslint-disable-next-line
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

function validate({
  email,
  password,
}: FormValues) {
  const obj: FormErrors = {}

  if (!email) {
    obj.email = reqMsg
  }
  else if (!emailRegex.test(email)) {
    obj.email = 'Þetta er ekki gilt tölvupóstfang'
  }

  if (!password) {
    obj.password = reqMsg
  }
  else if (password.length < 8) {
    obj.password = 'Þetta lykilorð er of stutt'
  }

  return obj
}

const fieldErrorCodeMap: Dictionary<string> = {
  INVALID_CREDENTIALS: 'Netfang eða lykilorð fannst ekki',
}

export default function Login (_: RouteComponentProps) {
  const location = useLocation()
  const {email, token} = qs.parse(location.search) as unknown as QueryParams
  const isConfirming = !!email && !!token
  const [accountConfirm, {
    data: confirmationData,
    loading: loadingConfirmation,
  }] = useMutation<AccountConfirm>(accountConfirmMutation)
  const [tokenCreate] = useMutation<TokenCreate>(tokenCreateMutation)
  const {setAuthToken} = useAuth()

  useEffect(() => {
    if (isConfirming) {
      accountConfirm({variables: {email, token}})
    }
  }, [email, token, accountConfirm, isConfirming])

  const user = confirmationData?.confirmAccount?.user
  const errors = confirmationData?.confirmAccount?.accountErrors

  const onSubmit = useCallback(async (
    {email, password}: FormValues,
    {setFieldError}: FormikHelpers<FormValues>,
  ) => {
    const {data: _data} = await tokenCreate({variables: {
      email,
      password,
    }})

    const token = _data?.tokenCreate?.token

    if (token) {
      setAuthToken(token)
      navigate('/verslun/vorur/')
    }

    _data?.tokenCreate?.accountErrors.forEach((err) => {
      if (err.field) {
        const msg = fieldErrorCodeMap[err.code]
        if (msg == null) {
          // TODO: Log to sentry
          console.error(`Got an undefined error code (${err.code}) for field: ${err.field}`)
        }
        setFieldError(err.field as keyof FormValues, msg ?? `Óþekkt villa (${err.code})`)
      }
    })
  }, [tokenCreate, setAuthToken])

  const formikProps: FormikConfig<FormValues> = {
    initialValues: {
      email: email || '',
      password: '',
    },
    onSubmit,
    validate,
  }

  if (loadingConfirmation) {
    return <Loader />
  }

  return (
    <Page>
      <Container size="lg">
        <div className="row justify-content-center pt-5">
          <div className="col-sm-8 col-xl-5">
            {isConfirming && (
              user ? (
                <div className="alert alert-success px-5 py-4" role="alert">
                  <h4 className="alert-heading">Aðgangur virkjaður!</h4>
                  <p className="mb-0">Nú getur þú skráð þig inn hér að neðan.</p>
                </div>
              ) : (
                <div className="alert alert-danger px-5 py-4" role="alert">
                  <h4 className="alert-heading">Virkjun mistókst!</h4>
                  {/* FIXME */}
                  <p className="mb-0">{JSON.stringify(errors)}</p>
                </div>
              )
            )}

            <div className="p-5 bg-white rounded border border-gray">
              <Formik component={LoginForm} {...formikProps} />
            </div>
          </div>
        </div>

        <div className="row justify-content-center py-4">
          <div className="col-sm-8 col-xl-5">
            <div className="px-5 py-4 bg-white rounded border border-gray d-flex align-items-center justify-content-between">
              <span className="text-muted">Ekki með aðgang?</span>
              <Link className="text-secondary" to="/skraning/">
                <PersonLinesFill size={18} className="mr-2" />
                Nýskráning
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </Page>
  )
}

function LoginForm({handleSubmit, isSubmitting}: FormikProps<FormValues>) {
  return (
    <form onSubmit={handleSubmit}>
      <div className="d-flex justify-content-between mb-5">
        <h1 className="h2">Innskráning</h1>
        <LockFill size={30} className="text-dark" />
      </div>

      <div className="mb-4">
        <Field name="email" type="text" label="Netfang" className="mb-3" />
        <Field name="password" type="password" label="Lykilorð" autoComplete="off" />
      </div>

      <button
        className="btn btn-primary w-100"
        type="submit"
        disabled={isSubmitting}
      >
        Innskrá
      </button>
    </form>
  )
}
